'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import MediaLibraryHeader from './MediaLibraryHeader';
import MediaFilters from './MediaFilters';
import MediaGrid from './MediaGrid';
import DispatchBar from './DispatchBar';
import DispatchModal from './DispatchModal';
import { mockSeries, mockMovies } from '@/lib/mockData';
import { loadCustomItems } from '@/app/settings/components/CustomItemsPanel';
import { configStore } from '@/lib/configStore';
import type { MediaItem, FilterState } from '@/lib/types';

export default function MediaLibraryScreen() {
  const [activeTab, setActiveTab] = useState<'all' | 'series' | 'movies' | 'custom'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: 'all',
    genre: 'all',
    source: 'all',
  });
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [isLoading] = useState(false);
  const [customItems, setCustomItems] = useState<MediaItem[]>([]);

  // Load custom items from localStorage on mount
  useEffect(() => {
    setCustomItems(loadCustomItems());
  }, []);

  const allItems: MediaItem[] = useMemo(() => {
    // Tag mock series with source instance info
    const sonarrInstances = configStore.getSonarrInstances();
    const radarrInstances = configStore.getRadarrInstances();

    const series = mockSeries.map((s) => ({
      ...s,
      mediaType: 'series' as const,
      sourceInstanceId: sonarrInstances[0]?.id ?? 'sonarr-default',
      sourceInstanceName: sonarrInstances[0]?.name ?? 'Sonarr',
    }));

    const movies = mockMovies.map((m) => ({
      ...m,
      mediaType: 'movie' as const,
      sourceInstanceId: radarrInstances[0]?.id ?? 'radarr-default',
      sourceInstanceName: radarrInstances[0]?.name ?? 'Radarr',
    }));

    return [...series, ...movies, ...customItems];
  }, [customItems]);

  const filteredItems = useMemo(() => {
    let items = allItems;

    if (activeTab === 'series') items = items.filter((i) => i.mediaType === 'series');
    if (activeTab === 'movies') items = items.filter((i) => i.mediaType === 'movie');
    if (activeTab === 'custom') items = items.filter((i) => i.mediaType === 'custom');

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      items = items.filter((i) => i.title.toLowerCase().includes(q));
    }
    if (filters.status !== 'all') {
      items = items.filter((i) => i.status.toLowerCase().replace(' ', '-') === filters.status);
    }
    if (filters.genre !== 'all') {
      items = items.filter((i) => i.genres.includes(filters.genre));
    }

    return items;
  }, [allItems, activeTab, filters]);

  const selectedItems = useMemo(
    () => allItems.filter((i) => selectedIds.has(i.id)),
    [allItems, selectedIds]
  );

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredItems.map((i) => i.id)));
  }, [filteredItems]);

  const clearAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const seriesCount = allItems.filter((i) => i.mediaType === 'series').length;
  const moviesCount = allItems.filter((i) => i.mediaType === 'movie').length;
  const customCount = allItems.filter((i) => i.mediaType === 'custom').length;

  return (
    <div className="flex flex-col min-h-full">
      <MediaLibraryHeader
        activeTab={activeTab}
        onTabChange={setActiveTab as (tab: string) => void}
        seriesCount={seriesCount}
        moviesCount={moviesCount}
        totalCount={allItems.length}
        selectedCount={selectedIds.size}
        onSelectAll={selectAll}
        onClearAll={clearAll}
        filteredCount={filteredItems.length}
        customCount={customCount}
      />

      <div className="px-6 xl:px-8 2xl:px-10 pb-2">
        <MediaFilters filters={filters} onFiltersChange={setFilters} />
      </div>

      <div className="flex-1 px-6 xl:px-8 2xl:px-10 pb-32">
        <MediaGrid
          items={filteredItems}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          isLoading={isLoading}
        />
      </div>

      {selectedIds.size > 0 && (
        <DispatchBar
          selectedCount={selectedIds.size}
          selectedItems={selectedItems}
          onDispatch={() => setShowDispatchModal(true)}
          onClear={clearAll}
        />
      )}

      {showDispatchModal && (
        <DispatchModal
          selectedItems={selectedItems}
          onClose={() => setShowDispatchModal(false)}
          onComplete={() => {
            setShowDispatchModal(false);
            clearAll();
          }}
        />
      )}
    </div>
  );
}